import React from 'react'
import {Box, Link, Stack, Typography} from '@mui/material'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import '../../../../utils/quillSafeClipboard'
import WbTextField from '../../../common/WbTextField'
import WbFileUpload from '../../../common/WbFileUpload'
import {Config} from '../../../../utils/Config'
import {IMAGE_TEXT_MAX_LENGTH} from '../../../../utils/contentScreen'

// Only formatting respondents will see: the server keeps paragraphs, bold,
// italic, underline, lists and links, and unwraps anything else to its text.
const TOOLBAR = [
  ['bold', 'italic', 'underline'],
  [{list: 'ordered'}, {list: 'bullet'}],
  ['link'],
]

const IMAGE_POSITIONS = [
  {value: 0, label: 'Above the text'},
  {value: 1, label: 'Below the text'},
]

export default function ContentScreenEditor({content, onChange, errors = []}) {
  const update = changes => onChange({...content, ...changes})
  const errorFor = code => errors.find(e => e.code === code)?.message
  const image = content?.image?.key ? content.image : null

  return (
    <Stack spacing={2} className="content-screen-editor" sx={{p: 2}}>
      <WbTextField
        label="Headline"
        value={content?.headline || ''}
        onChange={e => update({headline: e.target.value})}
        inputProps={{maxLength: 200}}
      />
      <WbTextField
        label="Sub-heading (optional)"
        value={content?.subheading || ''}
        onChange={e => update({subheading: e.target.value})}
        inputProps={{maxLength: 300}}
      />
      <Box>
        <Typography variant="body2" sx={{mb: 1}}>
          Text
        </Typography>
        <ReactQuill
          theme="snow"
          modules={{toolbar: TOOLBAR}}
          value={content?.bodyHtml || ''}
          placeholder="Text shown to respondents"
          onChange={html => update({bodyHtml: html})}
        />
        {errorFor('BODY_TOO_LONG') && (
          <Typography color="error" variant="body2" role="alert">
            {errorFor('BODY_TOO_LONG')}
          </Typography>
        )}
      </Box>
      <Box>
        <Typography variant="body2" sx={{mb: 1}}>
          Image (optional)
        </Typography>
        {image ? (
          <Stack spacing={2}>
            <Box
              component="img"
              src={`${Config.BASE_URL}/Artifact/download?fileName=${encodeURIComponent(image.key)}`}
              alt={image.altText || ''}
              sx={{maxWidth: 240, height: 'auto'}}
            />
            <Stack direction="row" spacing={2}>
              <WbFileUpload
                setImageName={key => update({image: {...image, key}})}
              />
              <Link
                component="button"
                type="button"
                color="#E84F6B"
                underline="hover"
                onClick={() => update({image: null})}
              >
                Remove image
              </Link>
            </Stack>
            <WbTextField
              label="Alt text (required)"
              value={image.altText || ''}
              onChange={e => update({image: {...image, altText: e.target.value}})}
              inputProps={{maxLength: IMAGE_TEXT_MAX_LENGTH}}
              error={!!errorFor('IMAGE_ALT_REQUIRED')}
              helperText={
                errorFor('IMAGE_ALT_REQUIRED') ||
                'Describes the image for people using screen readers.'
              }
            />
            <WbTextField
              label="Caption (optional)"
              value={image.caption || ''}
              onChange={e =>
                update({image: {...image, caption: e.target.value}})
              }
              inputProps={{maxLength: IMAGE_TEXT_MAX_LENGTH}}
            />
            <WbTextField
              select
              SelectProps={{native: true}}
              InputLabelProps={{shrink: true}}
              label="Image position"
              value={content?.imagePosition === 1 ? 1 : 0}
              onChange={e => update({imagePosition: Number(e.target.value)})}
            >
              {IMAGE_POSITIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </WbTextField>
          </Stack>
        ) : (
          <WbFileUpload
            button
            setImageName={key => update({image: {key, altText: '', caption: ''}})}
          />
        )}
      </Box>
      {errorFor('EMPTY') && (
        <Typography color="error" variant="body2" role="alert">
          {errorFor('EMPTY')}
        </Typography>
      )}
    </Stack>
  )
}
